import { ReactNode } from 'react';

export type { ItemWithGroup, AssigneeGroup } from '@adsk/alloy-assignee-picker';

interface Identifiable {
  id: string;
}

export interface RevisionFolder {
  folderUrn: string;
  folderUrnCreatedAt: string;
  revision: number;
}

export interface PermittedAction {
  id: string;
  fields: { [key: string]: string[] };
  mandatoryFields: string[];
  transitions: Transition[];
}

export interface Transition {
  id: string;
  name: string;
  actionId: string;
  stateFrom: State;
  stateTo: State;
  mandatoryFields?: string[];
  transitionFields?: string[];
}

export interface State {
  id: string;
  name: string;
}

export enum Priority {
  HIGH = 'High',
  NORMAL = 'Normal',
  LOW = 'Low',
}

export type ItemState =
  | 'sbc-1'
  | 'mgr-1'
  | 'rev'
  | 'mgr-2'
  | 'sbc-2'
  | 'draft'
  | 'void';

export enum TaskStatus {
  NOT_STARTED = 'not-started',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
}

export interface Task {
  id: string;
  assignedTo: string;
  assignedToType: string;
  isRequired: boolean;
  userName?: string;
  status: TaskStatus;
}

export interface ReviewStep {
  stepNumber: number;
  id: string;
  itemId: string;
  status: string;
  tasks: Task[];
}

// Value structure for single select custom attributes
export interface SingleSelectValue {
  value: string;
  isRemoved: boolean;
}

export interface MultiSelectValue {
  value: string;
  isRemoved: boolean;
}

export interface CustomAttribute {
  id: string;
  name: string;
  type: string;
  description?: string;
  options?: Array<{
    id: string;
    name: string;
    value: string | boolean;
  }>;
  value?:
    | string
    | number
    | boolean
    | Date
    | null
    | undefined
    | Record<string, string>
    | SingleSelectValue
    | MultiSelectValue[];
}

export interface SubmittalsItemRawData extends Identifiable {
  identifier: number;
  title: string;
  assignedTo: string;
  ccList: string[];
  consultantsList: string[];
  manager: string;
  subcontractor: string;
  specId: string;
  revision: number;
  createdAt: string;
  createdBy: string;
  priority: Priority;
  stateId: ItemState;
  description: string;
  assignedToField: string;
  responseComment: string;
  coReviewersList: string[];
  ballInCourt: string[];
  ballInCourtUsers: string[];
  ballInCourtCompanies: string[];
  ballInCourtRoles: string[];
  revisionsFoldersUrns: { [key: string]: RevisionFolder };
  folderUrn: string;
  specIdentifier: string;
  specTitle: string;
  typeId: string;
  statusId: string;
  distributionList: string[];
  updatedAt: string;
  updatedBy: string;
  requiredDate: string;
  permittedActions: PermittedAction[];
  responseId?: string;
  respondedBy?: string;
  respondedAt?: string;
  sentToSubmitter?: string;
  submitterDueDate?: string;
  receivedFromSubmitter?: string;
  managerDueDate?: string;
  sentToReviewer?: string;
  reviewerDueDate?: string;
  receivedFromReviewer?: string;
  publishedDate?: string;
  packageId?: string;
  packageIdentifier?: number;
  packageSpecIdentifier?: string;
  packageTitle?: string;
  requiredOnJobDate?: string;
  subsection?: string;
  steps: ReviewStep[];
  requiredApprovalDate?: string;
  leadTime?: number;
  dueDate?: string;
  reviewer?: string;
  customIdentifierHumanReadable?: string;
  customAttributes?: CustomAttribute[];
}

interface Pagination {
  limit: number;
  nextUrl?: string;
  offset: number;
  totalResults: number;
}

/**
 * Response from service for item list endpoint
 */
export interface SubmittalsListResponse {
  results: SubmittalsItemRawData[];
  pagination: Pagination;
}

export interface Spec {
  id: string;
  identifier: string;
  title: string;
}

export interface Package {
  id: string;
  identifier: number;
  title: string;
  specIdentifier: string;
}

export interface SubmittalsItem extends Omit<
  SubmittalsItemRawData,
  'specId' | 'specIdentifier' | 'specTitle' | 'dueDate' | 'updatedAt'
> {
  spec: Spec;
  package: Package;
  updatedAt: Date;
  dueDate?: Date;
}

export interface SpecRawData extends Identifiable {
  identifier: string;
  title: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface SpecsResponse {
  results: SpecRawData[];
  pagination: Pagination;
}

// Each filter option must have a string value
// can possibly contain more attributes of various types
export interface FilterOption {
  value: string;
  label?: string | ReactNode;

  [key: string]: unknown;
}

export interface StatusOption extends FilterOption {
  color: string;
  statusText?: ReactNode;
  stateText?: ReactNode;
}

export interface CustomAttributesData {
  id: string;
  name: string;
  type: string;
  description?: string;
  options?: Array<{
    id: string;
    name: string;
    value: string | boolean;
  }>;
}

export interface CustomAttributesResponse {
  results: CustomAttributesData[];
  pagination: Pagination;
}
